import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, todayLocal, exportFilename } from "@/lib/timefmt";
import { taskStatus, STATUS_META } from "@/lib/taskStatus";
import { toast } from "sonner";
import { Lock, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

const durMin = (t) => {
  const [sh, sm] = t.start.split(":").map(Number);
  const [eh, em] = t.end.split(":").map(Number);
  let d = (eh * 60 + em) - (sh * 60 + sm);
  return d <= 0 ? d + 1440 : d;
};
const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
const hm = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const COLUMNS = ["date", "type", "name", "status", "time", "total", "equipment", "locations", "crew", "logged"];

export default function TaskHistoryPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const todayStr = todayLocal();
  const [range, setRange] = useState({ from: shiftDays(todayLocal(), -7), to: shiftDays(todayLocal(), 7) });
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [filters, setFilters] = useState(Object.fromEntries(COLUMNS.map((c) => [c, ""])));
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

  useEffect(() => {
    setLoading(true);
    api.get(`/tasks?from_date=${range.from}&to_date=${range.to}`)
      .then((r) => setTasks(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  const extendRange = (dir) => setRange((r) => dir < 0 ? { ...r, from: shiftDays(r.from, -7) } : { ...r, to: shiftDays(r.to, 7) });
  const resetRange = () => setRange({ from: shiftDays(todayStr, -7), to: shiftDays(todayStr, 7) });

  const decorated = useMemo(() => tasks.map((t) => {
    const _status = taskStatus(t, null, todayStr, nowMin);
    const _total = durMin(t) + (t.brief_min || 0) + (t.debrief_min || 0);
    return {
      ...t,
      _status,
      _statusLabel: (STATUS_META[_status] || {}).label || "",
      _total,
      _startMin: hm(t.start),
      _text: {
        date: fmtDate(t.date),
        type: `${t.task_type_name}${t.kind === "commitment" ? " commitment" : ""}`,
        name: `${t.name || ""} ${t.remarks || ""}`,
        status: (STATUS_META[_status] || {}).label || "",
        time: `${t.start}–${t.end}`,
        total: fmtDur(_total),
        equipment: (t.equipment_labels || []).join(", "),
        locations: [t.primary_location_name, ...(t.additional_location_names || [])].filter(Boolean).join(", "),
        crew: (t.participants || []).map((p) => p.name).join(", "),
        logged: t.created_by === user?.id ? "me" : (t.created_by_name || ""),
      },
    };
  }), [tasks, todayStr, nowMin, user]);

  const textOf = (t) => t._text;

  const keyOf = (t, k) => (
    k === "date" ? `${t.date} ${t.start}`
      : k === "time" ? t._startMin
      : k === "total" ? t._total
      : String(t._text[k] ?? "").toLowerCase()
  );

  const view = useMemo(
    () => sortRows(filterRows(decorated, textOf, filters), sort, keyOf),
    [decorated, filters, sort] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const download = (content, filename, type) => {
    const url = URL.createObjectURL(content instanceof Blob ? content : new Blob([content], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const extMapFor = async () => {
    const m = { [user?.id]: user?.external_user_id || "" };
    try {
      const r = await api.get("/org/unit-members");
      for (const u of r.data) m[u.id] = u.external_user_id || "";
    } catch { /* unit members unavailable — external IDs left blank */ }
    return m;
  };

  const exportCsv = async () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const extMap = await extMapFor();
    const header = ["Date", "Task Type", "Name", "Kind", "Status", "Start", "End", "Brief (min)", "Debrief (min)", "Total (min)", "Remarks", "Equipment", "Primary Location", "Additional Locations", "Crew", "Logged by", "External ID"];
    const lines = [header.join(",")];
    for (const t of view) {
      lines.push([
        t.date, t.task_type_name, t.name || "", t.kind, t._status,
        t.start, t.end, t.brief_min ?? 0, t.debrief_min ?? 0, t._total,
        t.remarks || "", (t.equipment_labels || []).join("; "),
        t.primary_location_name || "", (t.additional_location_names || []).join("; "),
        (t.participants || []).map((p) => p.name).join("; "), t.created_by === user?.id ? "me" : t.created_by_name,
        extMap[t.created_by] || "",
      ].map(esc).join(","));
    }
    download(lines.join("\n"), exportFilename("task-history", "csv", user), "text/csv");
    toast.success(`Exported ${view.length} task(s) to CSV`);
  };

  const exportXlsx = async () => {
    try {
      const res = await api.post("/tasks/export/query", { ids: view.map((t) => t.id), format: "xlsx" }, { responseType: "blob" });
      download(res.data, exportFilename("task-history", "xlsx", user), "");
      toast.success(`Exported ${view.length} task(s) to XLSX`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{(user?.terminology?.task_label || "Task")} History</h1>
        <p className="text-slate-400 mt-1 text-sm">Tasks you've logged or been assigned to. Showing the last 7 days through the next 7 days by default — load more as needed. Click a column header to sort; use the filter row to narrow down.</p>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="task-history-toolbar">
        <button onClick={() => extendRange(-1)} data-testid="task-history-older"
          className="inline-flex items-center gap-1 px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-sm text-slate-300 transition-colors">
          <ChevronLeft className="h-3.5 w-3.5" /> Older 7 days
        </button>
        <span className="text-xs font-mono-num text-slate-400 px-1" data-testid="task-history-range">
          {fmtDate(range.from)} → {fmtDate(range.to)}
        </span>
        <button onClick={() => extendRange(1)} data-testid="task-history-newer"
          className="inline-flex items-center gap-1 px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-sm text-slate-300 transition-colors">
          Newer 7 days <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button onClick={resetRange} data-testid="task-history-reset"
          className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-xs text-slate-400 transition-colors">
          Reset
        </button>
        <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-slate-500 font-mono-num ml-1">Export this view:</span>
        <button onClick={exportCsv} disabled={!view.length} data-testid="task-history-export-csv"
          title="Download the current filtered/sorted view as CSV"
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-40 transition-colors">
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
        <button onClick={exportXlsx} disabled={!view.length} data-testid="task-history-export-xlsx"
          title="Download the current filtered/sorted view as XLSX"
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-40 transition-colors">
          <Download className="h-3.5 w-3.5" /> XLSX
        </button>
      </div>

      <div className="text-[11px] font-mono-num text-slate-500 mb-3" data-testid="task-history-count">
        {loading ? "Loading…" : `${view.length} task(s)`}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden overflow-x-auto">
        <table className="w-full" data-testid="task-history-table">
          <thead>
            <tr className="border-b border-slate-800/60 text-left">
              <SortTh label="Date" k="date" sort={sort} setSort={setSort} />
              <SortTh label="Type" k="type" sort={sort} setSort={setSort} />
              <SortTh label="Name" k="name" sort={sort} setSort={setSort} />
              <SortTh label="Status" k="status" sort={sort} setSort={setSort} />
              <SortTh label="Time" k="time" sort={sort} setSort={setSort} />
              <SortTh label="Total" k="total" sort={sort} setSort={setSort} />
              <SortTh label="Equipment" k="equipment" sort={sort} setSort={setSort} />
              <SortTh label="Locations" k="locations" sort={sort} setSort={setSort} />
              <SortTh label="Crew" k="crew" sort={sort} setSort={setSort} />
              <SortTh label="Logged by" k="logged" sort={sort} setSort={setSort} />
            </tr>
            <tr className="border-b border-slate-800">
              <FilterTh value={filters.date} onChange={setF("date")} testId="task-history-filter-date" />
              <FilterTh value={filters.type} onChange={setF("type")} testId="task-history-filter-type" />
              <FilterTh value={filters.name} onChange={setF("name")} testId="task-history-filter-name" />
              <FilterTh value={filters.status} onChange={setF("status")} testId="task-history-filter-status" />
              <FilterTh value={filters.time} onChange={setF("time")} testId="task-history-filter-time" />
              <FilterTh value={filters.total} onChange={setF("total")} testId="task-history-filter-total" />
              <FilterTh value={filters.equipment} onChange={setF("equipment")} testId="task-history-filter-equipment" />
              <FilterTh value={filters.locations} onChange={setF("locations")} testId="task-history-filter-locations" />
              <FilterTh value={filters.crew} onChange={setF("crew")} testId="task-history-filter-crew" />
              <FilterTh value={filters.logged} onChange={setF("logged")} testId="task-history-filter-logged" />
            </tr>
          </thead>
          <tbody>
            {!loading && view.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-6 text-sm text-slate-500">No tasks match — try widening the date range or clearing filters.</td></tr>
            )}
            {view.map((t) => {
              const m = STATUS_META[t._status] || { cls: "", label: t._status };
              return (
                <tr key={t.id} className={`border-b border-slate-800/70 align-top ${t._status === "cancelled" ? "opacity-70" : ""}`} data-testid={`task-history-row-${t.id}`}>
                  <td className="px-3 py-3 font-mono-num text-xs whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">
                    {t.task_type_name}
                    {t.kind === "commitment" && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-widest bg-violet-600/15 text-violet-300 border border-violet-600/40">Cmt</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm min-w-[140px]">
                    <span className="font-medium inline-flex items-center gap-1.5">
                      {t.name || "—"}
                      {t.fixed && <span title="Fixed — not moveable"><Lock className="h-3 w-3 text-slate-500" /></span>}
                    </span>
                    {t.remarks && <div className="text-[11px] text-slate-500 italic mt-0.5">{t.remarks}</div>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${m.cls}`}>{m.label}</span>
                  </td>
                  <td className="px-3 py-3 font-mono-num text-xs whitespace-nowrap">
                    {t.start}–{t.end}
                    <div className="text-[10px] text-slate-500">+{t.brief_min}m brief / +{t.debrief_min}m debrief</div>
                  </td>
                  <td className="px-3 py-3 font-mono-num text-xs whitespace-nowrap">{fmtDur(t._total)}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(t.equipment_labels || []).map((l) => (
                        <span key={l} className="px-1.5 py-0.5 rounded bg-emerald-600/10 text-emerald-300 border border-emerald-600/30 text-[10px] whitespace-nowrap">{l}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {t.primary_location_name && (
                        <span className="px-1.5 py-0.5 rounded bg-sky-600/10 text-sky-300 border border-sky-600/30 text-[10px] whitespace-nowrap">{t.primary_location_name}</span>
                      )}
                      {(t.additional_location_names || []).map((n) => (
                        <span key={n} className="px-1.5 py-0.5 rounded bg-sky-600/5 text-sky-400/80 border border-sky-600/20 text-[10px] whitespace-nowrap">{n}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(t.participants || []).map((p) => (
                        <span key={p.user_id} className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 text-[10px] whitespace-nowrap">{p.name}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">{t.created_by === user?.id ? "me" : t.created_by_name}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
